// ETF Wealth Builder Calculator -- the input form.
//
// Kept strictly as a PURE UI component: it owns only its local form state and
// delegates the actual network call upward via `onSubmit`. The parent
// orchestrator wires it to the TanStack mutation.

import React, { useState } from 'react';

import type { SimulationRequest } from '../types';

interface CalculatorFormProps {
    onSubmit: (data: SimulationRequest) => void;
    isPending: boolean;
    error: Error | null;
}

const AVAILABLE_ETFS = [
    {
        group: 'European Users (UCITS)',
        items: [
            { ticker: 'VWCE.DE', name: 'Vanguard FTSE All-World UCITS' },
            { ticker: 'SXR8.DE', name: 'iShares Core S&P 500 UCITS' },
            { ticker: 'IUSQ.DE', name: 'iShares MSCI ACWI UCITS (Acc)' },
            { ticker: 'EUNL.DE', name: 'iShares Core MSCI World UCITS (Acc)' },
            { ticker: 'XDWD.DE', name: 'Xtrackers MSCI World UCITS 1C' },
            { ticker: 'SPYY.DE', name: 'SPDR MSCI ACWI UCITS' },
        ],
    },
    {
        group: 'Global Users (Standard)',
        items: [
            { ticker: 'VT', name: 'Vanguard Total World Stock' },
            { ticker: 'VOO', name: 'Vanguard S&P 500' },
            { ticker: 'ACWI', name: 'iShares MSCI ACWI' },
            { ticker: 'URTH', name: 'iShares MSCI World' },
            { ticker: 'IXUS', name: 'iShares Core MSCI Total International Stock' },
            { ticker: 'VEU', name: 'Vanguard FTSE All-World ex-US' },
        ],
    },
];

export const CalculatorForm: React.FC<CalculatorFormProps> = ({
    onSubmit,
    isPending,
    error,
}) => {
    const [formData, setFormData] = useState<SimulationRequest>({
        ticker: 'VWCE.DE',
        initial_investment: 10000,
        monthly_contribution: 1000,
        years: 30,
        inflation_rate_pct: 2.5,
        yearly_step_up_pct: 3.0,
    });

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: name === 'ticker' ? value : Number(value),
        }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData);
    };

    return (
        <form
            onSubmit={handleSubmit}
            className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 space-y-5"
        >
            <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Choose ETF as an example for your Investment
                </label>
                <select
                    name="ticker"
                    value={formData.ticker}
                    onChange={handleChange}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 [color-scheme:light] outline-none focus:ring-2 focus:ring-sky-500"
                >
                    {AVAILABLE_ETFS.map((group) => (
                        <optgroup key={group.group} label={group.group}>
                            {group.items.map((etf) => (
                                <option key={etf.ticker} value={etf.ticker}>
                                    {etf.name} ({etf.ticker})
                                </option>
                            ))}
                        </optgroup>
                    ))}
                </select>
            </div>

            <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Initial Deposit
                </label>
                <input
                    type="number"
                    name="initial_investment"
                    value={formData.initial_investment}
                    onChange={handleChange}
                    min="0"
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 placeholder:text-slate-400 [color-scheme:light] outline-none focus:ring-2 focus:ring-sky-500"
                />
            </div>

            <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Monthly Deposit
                </label>
                <input
                    type="number"
                    name="monthly_contribution"
                    value={formData.monthly_contribution}
                    onChange={handleChange}
                    min="0"
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 placeholder:text-slate-400 [color-scheme:light] outline-none focus:ring-2 focus:ring-sky-500"
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                        Investment Period (Years)
                    </label>
                    <input
                        type="number"
                        name="years"
                        value={formData.years}
                        onChange={handleChange}
                        min="1"
                        max="40"
                        className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 placeholder:text-slate-400 [color-scheme:light] outline-none focus:ring-2 focus:ring-sky-500"
                    />
                </div>
                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                        Annual Growth Rate (%)
                    </label>
                    <input
                        type="number"
                        name="yearly_step_up_pct"
                        value={formData.yearly_step_up_pct}
                        onChange={handleChange}
                        step="0.1"
                        min="0"
                        className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 placeholder:text-slate-400 [color-scheme:light] outline-none focus:ring-2 focus:ring-sky-500"
                    />
                </div>
            </div>

            <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Annual Inflation Rate(%)
                </label>
                <input
                    type="number"
                    name="inflation_rate_pct"
                    value={formData.inflation_rate_pct}
                    onChange={handleChange}
                    step="0.1"
                    min="0"
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 placeholder:text-slate-400 [color-scheme:light] outline-none focus:ring-2 focus:ring-sky-500"
                />
            </div>

            <button
                type="submit"
                disabled={isPending}
                className="w-full mt-2 bg-slate-900 hover:bg-slate-800 text-white font-bold py-3.5 rounded-xl transition-all disabled:opacity-70 flex justify-center items-center"
            >
                {isPending ? 'Simulating 10,000 Futures...' : 'Run Stochastic Simulation'}
            </button>

            {error && (
                <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm border border-red-100 mt-2 font-medium">
                    {error.message}
                </div>
            )}
        </form>
    );
};
